import { useCallback, useEffect, useState } from 'react';
import { Box, GlobalStyles } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useTour } from '@reactour/tour';
import { TourStep } from './tourTypes';

// "Hold to peek": while the button is pressed, hide the tour (dark overlay +
// popover) so the operator can see the real UI underneath, then bring it back on
// release. We DON'T touch reactour's isOpen — closing it would fire our outcome
// logging / intro-completion side effects and reset the flow. Instead we just
// hide reactour's own elements via CSS (its default .reactour__mask /
// .reactour__popover classes) by toggling a class on <body>. Everything stays
// mounted, so currentStep, anchors and positioning survive the peek untouched.
const PEEK_CLASS = 'tour-peek';

// !important because reactour sets opacity via INLINE styles on both elements
// (maskWrapper opacity, popover), and inline styles beat a normal stylesheet rule.
const peekStyles = (
	<GlobalStyles
		styles={{
			[`body.${PEEK_CLASS} .reactour__mask`]: {
				opacity: '0 !important',
				pointerEvents: 'none !important',
				transition: 'opacity 90ms ease',
			},
			[`body.${PEEK_CLASS} .reactour__popover`]: {
				opacity: '0 !important',
				pointerEvents: 'none !important',
				transition: 'opacity 90ms ease',
			},
		}}
	/>
);

const TourPeekButton = () => {
	const { isOpen, currentStep, steps } = useTour();
	const [peeking, setPeeking] = useState(false);
	const peekPlacement =
		(steps?.[currentStep] as TourStep | undefined)?.peekPlacement ?? 'right';

	const stop = useCallback(() => {
		document.body.classList.remove(PEEK_CLASS);
		setPeeking(false);
	}, []);

	const start = useCallback(
		(e: React.PointerEvent<HTMLButtonElement>) => {
			// Capture the pointer so we still get the release even if it drifts off
			// the button (or the button ends up hidden) during the hold.
			e.currentTarget.setPointerCapture?.(e.pointerId);
			document.body.classList.add(PEEK_CLASS);
			setPeeking(true);
		},
		[],
	);

	// Safety net: end the peek on any global release, and always clean up the body
	// class when the tour closes or this unmounts mid-hold.
	useEffect(() => {
		if (!peeking) return;
		window.addEventListener('pointerup', stop);
		window.addEventListener('pointercancel', stop);
		return () => {
			window.removeEventListener('pointerup', stop);
			window.removeEventListener('pointercancel', stop);
		};
	}, [peeking, stop]);

	useEffect(() => {
		if (!isOpen) stop();
		return () => stop();
	}, [isOpen, stop]);

	if (!isOpen) return null;

	return (
		<>
			{peekStyles}
			<Box
				component="button"
				type="button"
				aria-label="Hold to peek at the page"
				onPointerDown={start}
				onPointerUp={stop}
				onContextMenu={(e) => e.preventDefault()}
				sx={{
					position: 'fixed',
					bottom: 20,
					left: peekPlacement === 'left' ? 20 : 'auto',
					right: peekPlacement === 'right' ? 20 : 'auto',
					zIndex: 100000, // above reactour's mask (99999)
					display: 'flex',
					alignItems: 'center',
					gap: 1,
					px: 1.75,
					py: 1,
					borderRadius: 999,
					border: '2px solid #000',
					background: '#eeeeee',
					color: 'text.primary',
					font: 'inherit',
					fontSize: 13,
					fontWeight: 600,
					cursor: 'pointer',
					userSelect: 'none',
					touchAction: 'none', // holding shouldn't scroll on touch
					boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
					opacity: peeking ? 0.55 : 1,
					transition: 'opacity 90ms ease',
				}}
			>
				<VisibilityIcon sx={{ fontSize: 18 }} />
				Hold to peek
			</Box>
		</>
	);
};

export default TourPeekButton;
